import {
  CSSProperties,
  memo,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'
import clsx from 'clsx'

import styles from './Collapse.module.scss'

type CollapseOrientation = 'horizontal' | 'vertical'

export interface CollapseProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  open: boolean
  transitionDuration?: number
  orientation?: CollapseOrientation
  unmountOnExit?: boolean
}

const CollapseComponent = ({
  children,
  open,
  transitionDuration = 300,
  orientation = 'vertical',
  unmountOnExit = false,
  className,
  style,
  ...props
}: CollapseProps) => {
  const [mounted, setMounted] = useState(open)
  const [size, setSize] = useState<number>(0)

  const contentRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!contentRef.current) return

    const newSize =
      orientation === 'vertical'
        ? contentRef.current.scrollHeight
        : contentRef.current.scrollWidth

    setSize(newSize)
  }, [children, orientation, mounted])

  useEffect(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    if (open) {
      if (!mounted) setMounted(true)

      return
    }

    hideTimeoutRef.current = setTimeout(() => {
      setMounted(false)
    }, transitionDuration)

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
    }
  }, [open, transitionDuration])

  if (unmountOnExit && !mounted) return null

  const isHorizontal = orientation === 'horizontal'

  const wrapperStyle = {
    '--collapse-size': open ? `${size}px` : '0px',
    '--collapse-duration': `${transitionDuration}ms`,
    ...style
  } as CSSProperties

  return (
    <div
      className={clsx(
        styles.collapse,
        isHorizontal ? styles.horizontal : styles.vertical,
        { [styles.unmounted]: !mounted },
        className
      )}
      style={wrapperStyle}
      aria-hidden={!mounted}
      {...props}
    >
      <div
        ref={contentRef}
        className={clsx(styles.content, {
          [styles.contentHorizontal]: isHorizontal
        })}
      >
        {children}
      </div>
    </div>
  )
}

const Collapse = memo(CollapseComponent)

export default Collapse
